import { useEffect, useMemo, useRef, useState } from "react";

import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import SegmentedButton from "@/components/SegmentedButton";
import SkeletonElement from "@/components/Skeleton";
import useAmplitude from "@/hooks/useAmplitude";
import { useIsMobile } from "@/hooks/useIsMobile";
import type { Section } from "@/types";

import SectionCoverFlow from "./SectionCoverFlow";
import SectionsCatalogTable from "./SectionsCatalogTable";
import { SECTION_ANALYTICS_EVENTS, getSectionAnalyticsData } from "../_utils/amplitude";
import { formatSectionLegacyId } from "../_utils/formatSectionLegacyId";
import { SECTIONS_CATALOG_VIEW_OPTIONS, type SectionsCatalogViewMode } from "../_utils/sectionsCatalogView";
import { resolveSectionsCatalogViewMode } from "../_utils/resolveSectionsCatalogViewMode";
import {
  persistSectionsCatalogViewMode,
  readStoredSectionsCatalogViewMode
} from "../_utils/sectionsCatalogViewStorage";

type Props = {
  sections: Section[];
  loading: boolean;
};

function matchesQuery(section: Section, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  if (section.name.toLowerCase().includes(normalized)) return true;

  const legacyId = formatSectionLegacyId(section.legacyId);
  if (legacyId?.toLowerCase().includes(normalized)) return true;

  return section.legacyId?.toLowerCase().includes(normalized) ?? false;
}

export default function SectionsCatalog({ sections, loading }: Props) {
  const { trackEvent } = useAmplitude();
  const isMobile = useIsMobile();
  const canBrowse = !isMobile;
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState<SectionsCatalogViewMode>(readStoredSectionsCatalogViewMode);
  const effectiveViewMode = resolveSectionsCatalogViewMode(viewMode, isMobile);
  const hasTrackedCatalogViewRef = useRef(false);

  useEffect(() => {
    if (loading || hasTrackedCatalogViewRef.current) return;

    hasTrackedCatalogViewRef.current = true;
    trackEvent(
      SECTION_ANALYTICS_EVENTS.catalogViewed,
      getSectionAnalyticsData({
        source: "section_catalog",
        viewMode: effectiveViewMode
      })
    );
  }, [effectiveViewMode, loading, trackEvent]);

  const sortedSections = useMemo(
    () => [...sections].sort((left, right) => left.name.localeCompare(right.name)),
    [sections]
  );

  const filteredSections = useMemo(
    () => sortedSections.filter((section) => matchesQuery(section, query)),
    [query, sortedSections]
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-end sm:justify-between">
          <SkeletonElement height="h-14" className="w-full sm:max-w-md flex-1 rounded-lg" />
          {canBrowse && <SkeletonElement height="h-10" width="w-44" className="rounded-full" />}
        </div>
        <SkeletonElement height="h-[28rem]" className="rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:max-w-md sm:flex-1">
          <SearchInput
            ariaLabel="Search Sections"
            placeholder="Search By Name or Section ID"
            query={query}
            onChange={setQuery}
            onClear={() => setQuery("")}
          />
        </div>
        {canBrowse && (
          <div className="w-full sm:w-auto">
            <SegmentedButton
              ariaLabel="Sections view"
              options={SECTIONS_CATALOG_VIEW_OPTIONS}
              value={viewMode}
              onChange={(value) => {
                if ((value === "browse" || value === "list") && value !== viewMode) {
                  trackEvent(
                    SECTION_ANALYTICS_EVENTS.catalogViewChanged,
                    getSectionAnalyticsData({
                      source: "section_catalog",
                      viewMode: value,
                      previousViewMode: viewMode
                    })
                  );
                  setViewMode(value);
                  persistSectionsCatalogViewMode(value);
                }
              }}
            />
          </div>
        )}
      </div>

      {filteredSections.length === 0 ? (
        <EmptyState
          iconName="search_off"
          title="No sections match your search"
          caption={query.trim() ? "Try a different name or section ID." : "No sections are available."}
        />
      ) : effectiveViewMode === "list" ? (
        <div data-testid="sections-catalog-list">
          <SectionsCatalogTable sections={filteredSections} />
        </div>
      ) : (
        <SectionCoverFlow sections={filteredSections} />
      )}
    </div>
  );
}
